import driverService from "../../api/services/driverService";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import SafeImage from "../../components/common/SafeImage";

export default function index() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [statusUpdating, setStatusUpdating] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("name"); // 'name', 'id', or 'email'

  // Helper to get full image URL
  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) {
      return "/avatar-placeholder.png"; // Default image from public folder
    }
    if (profileImage.startsWith("/storage/")) {
      return (
        (import.meta.env.VITE_API_URL || "http://localhost:8000") + profileImage
      );
    }
    return profileImage;
  };

  // Fetch drivers from API
  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const result = await driverService.getAllDrivers();
      if (result.success) {
        // Transform API data to match the expected format
        const transformedDrivers = result.data.map((driver) => ({
          Id: driver.driver_id,
          name: driver.user.name,
          email: driver.user.email,
          phone: driver.user.phone_no || "N/A",
          company: "GoHome Express",
          image: getProfileImageUrl(driver.user.profile_image),
          vechileNumber: driver.vehicle_no,
          status: driver.user.status,
          dateOfJoining: driver.created_at,
        }));
        setDrivers(transformedDrivers);
      } else {
        toast.error(result.message || "Failed to fetch drivers");
        setDrivers([]);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      toast.error("Failed to fetch drivers");
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const handleDeleteClick = (driver) => {
    setDriverToDelete(driver);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!driverToDelete) return;
    try {
      setDeleteLoading(true);
      const result = await driverService.deleteDriver(driverToDelete.Id);
      if (result.success) {
        toast.success("Driver deleted successfully");
        setDrivers((prevDrivers) =>
          prevDrivers.filter((d) => d.Id !== driverToDelete.Id),
        );
      } else {
        toast.error(result.message || "Failed to delete driver");
      }
    } catch (error) {
      console.error("Error deleting driver:", error);
      toast.error("Failed to delete driver");
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setDriverToDelete(null);
    }
  };

  // Filter drivers by status and search query
  const filteredDrivers = drivers.filter((driver) => {
    const matchesStatus =
      statusFilter === "all" || driver.status === statusFilter;
    const query = searchQuery.trim().toLowerCase();
    if (!query) return matchesStatus;

    let matches = false;
    if (filterType === "name") {
      matches = driver.name.toLowerCase().includes(query);
    } else if (filterType === "id") {
      matches = String(driver.Id) === query;
    } else if (filterType === "email") {
      matches = driver.email.toLowerCase().includes(query);
    }
    return matchesStatus && matches;
  });

  // Handle status change
  const handleStatusChange = async (driver, newStatus) => {
    setStatusUpdating((prev) => ({ ...prev, [driver.Id]: true }));
    try {
      const result = await driverService.updateDriver(driver.Id, {
        status: newStatus,
      });
      if (result.success) {
        setDrivers((prev) =>
          prev.map((d) =>
            d.Id === driver.Id ? { ...d, status: newStatus } : d,
          ),
        );
        toast.success("Status updated!");
      } else {
        toast.error(result.message || "Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setStatusUpdating((prev) => ({ ...prev, [driver.Id]: false }));
    }
  };

  if (loading) {
    return (
      <>
        <div className="row">
          <div className="col-sm-12">
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading drivers...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* All Drivers Table Start */}
      <div className="row">
        <div className="col-sm-12">
          <div>
            <div>
              <div className="title-header option-title d-flex align-items-center justify-content-between">
                <h2>All Drivers</h2>
                <div className="d-flex align-items-center">
                  <select
                    className="form-select form-select-sm w-auto me-2"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="name">Name</option>
                    <option value="id">ID</option>
                    <option value="email">Email</option>
                  </select>
                  <input
                    type="text"
                    className="form-control form-control-sm me-2"
                    style={{ width: 220 }}
                    placeholder={`Search by ${filterType}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <form className="d-inline-flex me-2">
                    <Link
                      to="/driveraddorupdate"
                      className="align-items-center btn btn-theme d-flex"
                    >
                      <i data-feather="plus"></i>Add Driver
                    </Link>
                  </form>
                  <select
                    className="form-select form-select-sm w-auto"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {filteredDrivers.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No drivers found</p>
                </div>
              ) : (
                <div className="table-responsive table-product">
                  <table
                    className="table all-package theme-table align-middle"
                    id="table_id"
                  >
                    <thead className="table-light">
                      <tr>
                        <th className="text-center">ID</th>
                        <th className="text-center">Driver</th>
                        <th className="text-start">Name</th>
                        <th className="text-start">Vehicle Number</th>
                        <th className="text-start">Phone</th>
                        <th className="text-strat">Email</th>
                        <th className="text-center">Action</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredDrivers.map((driver, keynum) => (
                        <tr key={keynum}>
                          <td className="text-center">
                            <div className="user-id">
                              <span>{driver.Id}</span>
                            </div>
                          </td>

                          <td className="text-center">
                            <div className="table-image">
                              <SafeImage
                                src={driver.image}
                                className="img-fluid "
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                                alt={driver.name}
                              />
                            </div>
                          </td>

                          <td>
                            <div className="user-name">
                              <span className="d-block fw-bold">
                                {driver.name}
                              </span>
                              <span className="text-muted small">
                                {driver.company}
                              </span>
                            </div>
                          </td>

                          <td className="text-start">{driver.vechileNumber}</td>

                          <td className="text-start">{driver.phone}</td>

                          <td className="text-start">{driver.email}</td>

                          <td
                            style={{ padding: "4px" }}
                            className="text-center"
                          >
                            <ul
                              className="list-inline mb-0 d-flex align-items-center justify-content-center"
                              style={{ gap: "4px" }}
                            >
                              <li className="list-inline-item">
                                <Link
                                  to={`/driverdetails/${driver.Id}`}
                                  className="btn btn-sm btn-light p-1"
                                  style={{ lineHeight: 1, minWidth: 0 }}
                                >
                                  <i
                                    className="ri-eye-line"
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "16px",
                                    }}
                                  ></i>
                                </Link>
                              </li>
                              <li className="list-inline-item">
                                <Link
                                  to={`/driveraddorupdate/${driver.Id}`}
                                  className="btn btn-sm btn-light p-1"
                                  style={{ lineHeight: 1, minWidth: 0 }}
                                >
                                  <i
                                    className="ri-pencil-line"
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "16px",
                                    }}
                                  ></i>
                                </Link>
                              </li>
                              <li className="list-inline-item">
                                <button
                                  onClick={() => handleDeleteClick(driver)}
                                  className="btn btn-sm btn-light p-1"
                                  style={{
                                    border: "none",
                                    background: "none",
                                    padding: 0,
                                    lineHeight: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <i
                                    className="ri-delete-bin-line"
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "16px",
                                      color: "#dc3545",
                                      verticalAlign: "middle",
                                    }}
                                  ></i>
                                </button>
                              </li>
                            </ul>
                          </td>

                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center">
                              <select
                                className={`form-select form-select-sm w-auto ${driver.status === "active" ? "border-success" : "border-danger"}`}
                                value={driver.status}
                                disabled={!!statusUpdating[driver.Id]}
                                onChange={(e) =>
                                  handleStatusChange(driver, e.target.value)
                                }
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                              {statusUpdating[driver.Id] && (
                                <span
                                  className="spinner-border spinner-border-sm ms-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete driver "{driverToDelete?.name}
                  "? This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
